import { motion } from "framer-motion";
import { Wallet, Coins, Landmark, Banknote, TrendingUp, RotateCcw, Repeat } from "lucide-react";

const flow = [
  { icon: Wallet, label: "Income", copy: "Money enters the household." },
  { icon: Coins, label: "Cash Flow", copy: "Organized, intentional monthly cash flow." },
  { icon: Landmark, label: "Family Banking System", copy: "Capital is stored inside a system you control.", highlight: true },
  { icon: Banknote, label: "Access Capital", copy: "Borrow against available capital when needed." },
  { icon: TrendingUp, label: "Deploy Capital", copy: "Purchase, protect, invest, or create income." },
  { icon: RotateCcw, label: "Rebuild Liquidity", copy: "Repay and restore the system for the next use." },
];

export const FamilyBankingFoundation = () => (
  <section data-testid="family-banking-foundation" className="section-lr bg-ivory">
    <div className="container-lr grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
      <div>
        <div className="flex items-center gap-4">
          <span className="gold-rule" />
          <p className="eyebrow">Why the Foundation Comes First</p>
        </div>
        <h2 className="section-heading mt-6 text-legacy">Control the Flow of <span className="accent-word">Capital</span> Before Asking It to Do More.</h2>
        <p className="mt-6 text-base leading-relaxed text-slate md:text-lg">A strong financial system begins with understanding how money enters the family, where it is stored, how it is accessed, and how it is replenished after use.</p>
      </div>
      <div className="relative overflow-hidden rounded-card border border-gold/30 bg-legacy p-6 text-ivory shadow-[0_24px_60px_-40px_rgba(17,17,17,0.5)] md:p-8">
        <p className="eyebrow text-gold">The Money Cycle</p>
        <div className="relative mt-6">
          {flow.map(({ icon: Icon, label, copy, highlight }, index) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className="relative flex items-start gap-4 pb-6 last:pb-0"
            >
              {index < flow.length - 1 && (
                <span aria-hidden="true" className="absolute left-[1.4375rem] top-12 h-[calc(100%-2.25rem)] w-px bg-gradient-to-b from-gold/60 to-gold/15" />
              )}
              <span
                className={`relative z-10 inline-flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-full border ${
                  highlight ? "border-gold bg-gold text-legacy shadow-[0_0_0_4px_rgba(197,161,90,0.18)]" : "border-gold/50 bg-white/[0.05] text-gold"
                }`}
              >
                <Icon size={22} strokeWidth={1.5} />
              </span>
              <div className="pt-1.5">
                <p className={`font-serif text-xl leading-tight ${highlight ? "text-gold" : "text-ivory"}`}>
                  {label}
                  {highlight && <span className="ml-2 align-middle text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-gold/80">Foundation</span>}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-taupe">{copy}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: flow.length * 0.08 }}
          className="mt-1 flex items-center gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3.5"
        >
          <Repeat size={20} strokeWidth={1.75} className="shrink-0 text-gold" />
          <p className="text-sm font-semibold leading-snug tracking-wide text-gold">Repeat &mdash; the system compounds as capital is reused over time.</p>
        </motion.div>
      </div>
    </div>
    <p className="container-lr mt-8 text-xs leading-relaxed text-slate/60">
      Educational note: this flow is a planning framework. It does not imply the same dollar exists in multiple places at once or that any strategy is appropriate for every household.
    </p>
  </section>
);
